import { z } from 'zod';
import { currencyCodeSchema } from './currency.js';
import { exchangeRateSchema } from './money.js';
/**
 * Family exchange rates (Phase 3), entered by hand. A rate is always "1 base = X quote", the way
 * Lebanese people say it: 1 USD = 89,500 LBP is stored as 89500 (CLAUDE.md §3.2). Rates are
 * append-only; a mistake is fixed by adding a new one. Transactions keep their own snapshot.
 */
/** After this many days the latest rate is "old" and the screens suggest updating it. */
export const RATE_STALE_AFTER_DAYS = 7;
export const rateLabelSchema = z.string().trim().max(40);
export const exchangeRateEntrySchema = z.object({
    id: z.string().uuid(),
    baseCurrency: currencyCodeSchema,
    quoteCurrency: currencyCodeSchema,
    /** Units of quoteCurrency per 1 baseCurrency, e.g. "89500.0000000000". */
    rate: exchangeRateSchema,
    label: z.string().nullable(),
    /** When the rate starts to apply (ISO 8601, UTC). */
    effectiveAt: z.string(),
    createdByMemberId: z.string().uuid().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
});
export const rateListQuerySchema = z.object({
    quoteCurrency: currencyCodeSchema.optional(),
    /** Opaque cursor from the previous page's nextCursor. */
    cursor: z.string().max(200).optional(),
    limit: z
        .string()
        .regex(/^[1-9]\d?$|^100$/, 'limit must be 1 to 100')
        .default('30')
        .transform((v) => Number.parseInt(v, 10)),
});
export const rateListResponseSchema = z.object({
    items: z.array(exchangeRateEntrySchema),
    nextCursor: z.string().nullable(),
});
export const currentRatesResponseSchema = z.object({
    baseCurrency: currencyCodeSchema,
    /** One row per enabled currency other than the base currency. */
    items: z.array(z.object({
        quoteCurrency: currencyCodeSchema,
        /** The latest rate, or null when none was ever entered. */
        current: exchangeRateEntrySchema.nullable(),
        /** True when there is no rate, or the latest is older than RATE_STALE_AFTER_DAYS. */
        stale: z.boolean(),
    })),
    /** Whether the viewer may add rates (rates.manage). */
    canManage: z.boolean(),
});
export const createRateRequestSchema = z.object({
    quoteCurrency: currencyCodeSchema,
    rate: exchangeRateSchema,
    /** Defaults to now. Can be earlier (a rate noted yesterday), never in the future. */
    effectiveAt: z.iso.datetime({ offset: true }).optional(),
    label: rateLabelSchema.nullish().transform((v) => (v ? v : null)),
});
//# sourceMappingURL=rates.js.map